import { secureRandomFloat } from './random';

export const POINTER_ANGLE_DEG = 270; // 12 o'clock (North)
export const POINTER_ANGLE_RAD = (POINTER_ANGLE_DEG * Math.PI) / 180;

export interface SpinTarget {
  winnerIndex: number;
  targetRotationDeg: number;
  totalRotations: number;
}

/**
 * Normalizes an angle in degrees to [0, 360)
 */
export function normalizeAngleDeg(angle: number): number {
  const mod = angle % 360;
  return mod < 0 ? mod + 360 : mod;
}

/**
 * Calculate the segment index located under the top pointer at a given wheel rotation
 */
export function getIndexUnderPointer(rotationDeg: number, totalSegments: number): number {
  if (totalSegments <= 0) return -1;
  if (totalSegments === 1) return 0;

  const segmentAngle = 360 / totalSegments;
  const currentNorm = normalizeAngleDeg(rotationDeg);
  
  // In local wheel coordinates, the angle currently aligned with POINTER_ANGLE_DEG (270) is:
  const localAngle = normalizeAngleDeg(POINTER_ANGLE_DEG - currentNorm);
  
  const index = Math.floor(localAngle / segmentAngle);
  return Math.min(Math.max(0, index), totalSegments - 1);
}

/**
 * Calculates the exact target rotation degrees needed to land squarely on the winnerIndex
 */
export function calculateTargetRotation(
  currentRotationDeg: number,
  winnerIndex: number,
  totalSegments: number,
  durationSeconds: number = 5
): SpinTarget {
  if (totalSegments <= 0) {
    return { winnerIndex: 0, targetRotationDeg: currentRotationDeg, totalRotations: 0 };
  }

  const segmentAngle = 360 / totalSegments;

  // Pick a safe margin within [0.2, 0.8] of the segment to avoid landing right on divider line
  const offsetRatio = 0.2 + secureRandomFloat() * 0.6; // between 0.2 and 0.8
  const targetLocalAngle = (winnerIndex + offsetRatio) * segmentAngle;

  // We want: (targetLocalAngle + finalRotation) % 360 === POINTER_ANGLE_DEG (270)
  // Therefore: finalRotationBase = (270 - targetLocalAngle) % 360
  const finalRotationBase = normalizeAngleDeg(POINTER_ANGLE_DEG - targetLocalAngle);

  // How many degrees forward from currentRotationDeg to reach finalRotationBase:
  const currentNorm = normalizeAngleDeg(currentRotationDeg);
  let forwardDelta = normalizeAngleDeg(finalRotationBase - currentNorm);

  // Ensure forwardDelta has at least a small forward turn
  if (forwardDelta < 45) {
    forwardDelta += 360;
  }

  // Calculate full spins based on duration (e.g. 5 sec -> ~6-8 full rotations)
  const baseSpins = Math.max(4, Math.floor(durationSeconds * 1.6));
  const totalSpins = baseSpins;
  const targetRotationDeg = currentRotationDeg + forwardDelta + totalSpins * 360;

  return {
    winnerIndex,
    targetRotationDeg,
    totalRotations: totalSpins,
  };
}

/**
 * Smooth custom ease-out curve (Ease Out Quart + gentle initial acceleration)
 */
export function easeOutCustom(t: number): number {
  // Clamp t between 0 and 1
  const clamped = Math.max(0, Math.min(1, t));
  // 1 - (1 - t)^4.2 for realistic friction deceleration
  return 1 - Math.pow(1 - clamped, 4.2);
}

/**
 * Instantaneous velocity of the easing curve at normalized progress t in [0, 1]
 */
export function easeOutCustomVelocity(t: number): number {
  const clamped = Math.max(0, Math.min(1, t));
  // Derivative of 1 - (1-t)^4.2 is 4.2 * (1-t)^3.2
  return 4.2 * Math.pow(1 - clamped, 3.2);
}

/**
 * Converts degree to radian
 */
export function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/**
 * Formats long text labels to fit nicely into wheel slices
 */
export function truncateLabel(label: string, maxLength: number = 22): string {
  if (label.length <= maxLength) return label;
  return label.substring(0, maxLength - 1) + '…';
}
